<script setup lang="ts">
import { onBeforeUnmount, onMounted, nextTick, ref, watch } from 'vue';

export type DropdownMenuVariant = 'menu' | 'select';

export interface DropdownMenuItem extends Record<string, unknown> {
  label: string;
  /** Optional second line rendered under the label (used by the `select` variant). */
  description?: string;
  /** Marks the item as the current selection — renders a check in the `select` variant. */
  selected?: boolean;
  disabled?: boolean;
  onSelect?: (ctx: { index: number }) => void;
}

interface Props {
  items?: DropdownMenuItem[];
  ariaLabel?: string;
  menuMinWidth?: string;
  /** `menu` (default) is the actions list; `select` is the two-line selector with a check on the selected item. */
  variant?: DropdownMenuVariant;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  ariaLabel: 'Menu options',
  menuMinWidth: '220px',
  variant: 'menu',
});

const emit = defineEmits<{
  (event: 'select', payload: { item: DropdownMenuItem; index: number }): void;
  (event: 'open'): void;
  (event: 'close'): void;
}>();

const isOpen = ref(false);
const root = ref<HTMLElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);

function toggle(): void {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
}

function open(): void {
  if (props.items.length === 0) return;
  isOpen.value = true;
  emit('open');
}

function close(): void {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close');
}

function onItemClick(item: DropdownMenuItem, index: number): void {
  if (item.disabled) return;
  item.onSelect?.({ index });
  emit('select', { item, index });
  close();
}

function onGlobalClick(event: MouseEvent): void {
  const el = root.value;
  if (!el) return;
  if (!el.contains(event.target as Node)) close();
}

function onEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && isOpen.value) {
    close();
    const trigger = root.value?.querySelector<HTMLButtonElement>('.pj-dropdown-menu__trigger');
    trigger?.focus();
  }
}

watch(isOpen, async (opened) => {
  if (!opened) return;
  await nextTick();
  const firstItem = menuEl.value?.querySelector<HTMLButtonElement>('.pj-dropdown-menu__item:not(:disabled)');
  firstItem?.focus();
});

onMounted(() => {
  window.addEventListener('click', onGlobalClick);
  window.addEventListener('keydown', onEscape);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', onGlobalClick);
  window.removeEventListener('keydown', onEscape);
});
</script>

<template>
  <div class="pj-dropdown-menu" ref="root" :class="{ 'is-open': isOpen, 'pj-dropdown-menu--select': variant === 'select' }">
    <button
      type="button"
      class="pj-dropdown-menu__trigger"
      :aria-expanded="isOpen"
      :aria-haspopup="'menu'"
      :aria-label="ariaLabel"
      @click.stop="toggle"
    >
      <slot name="trigger" :is-open="isOpen">
        <span class="pj-dropdown-menu__trigger-default">
          <span>Options</span>
          <svg
            class="pj-dropdown-menu__chevron"
            :class="{ 'is-open': isOpen }"
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            focusable="false"
            aria-hidden="true"
          >
            <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </slot>
    </button>

    <Transition name="pj-dropdown-menu-fade">
      <div
        v-if="isOpen"
        ref="menuEl"
        class="pj-dropdown-menu__menu"
        role="menu"
        :style="{ minWidth: menuMinWidth }"
      >
        <button
          v-for="(item, idx) in items"
          :key="idx"
          type="button"
          role="menuitem"
          class="pj-dropdown-menu__item"
          :class="{ 'is-disabled': item.disabled }"
          :disabled="item.disabled"
          :aria-disabled="item.disabled ? 'true' : undefined"
          @click="onItemClick(item, idx)"
        >
          <span v-if="$slots['item-icon']" class="pj-dropdown-menu__item-icon">
            <slot name="item-icon" :item="item" :index="idx" />
          </span>
          <span class="pj-dropdown-menu__item-text">
            <span class="pj-dropdown-menu__item-label">{{ item.label }}</span>
            <span v-if="item.description" class="pj-dropdown-menu__item-description">{{ item.description }}</span>
          </span>
          <span v-if="variant === 'select' && item.selected" class="pj-dropdown-menu__item-check" aria-hidden="true">
            <svg width="24" height="24" viewBox="-2.75 -5.75 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
              <path d="M1 7.75L6.25 13L18.25 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.pj-dropdown-menu {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.pj-dropdown-menu__trigger {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-radius: var(--pj-radius-sm);

  &:focus { outline: none; }

  &:focus-visible {
    box-shadow: 0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }
}

.pj-dropdown-menu__trigger-default {
  display: inline-flex;
  align-items: center;
  gap: var(--pj-space-2);
  background: var(--pj-color-surface-default);
  border: var(--pj-border-width-hairline) solid var(--pj-color-border-default);
  border-radius: var(--pj-radius-md);
  padding: var(--pj-space-2) var(--pj-space-3);
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-sm);
  font-weight: var(--pj-font-weight-medium);
  color: var(--pj-color-text-default);
  line-height: var(--pj-font-line-height-tight);
  box-shadow: var(--pj-shadow-resting);
  transition:
    background-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    border-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    box-shadow var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);
}

.pj-dropdown-menu__trigger:hover .pj-dropdown-menu__trigger-default {
  border-color: var(--pj-color-border-hover);
  box-shadow: var(--pj-shadow-hover);
}

.pj-dropdown-menu__chevron {
  transition: transform var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);

  &.is-open { transform: rotate(180deg); }
}

.pj-dropdown-menu__menu {
  position: absolute;
  top: calc(100% + var(--pj-space-1));
  left: 50%;
  transform: translateX(-50%);
  background: var(--pj-color-surface-default);
  border: var(--pj-border-width-hairline) solid var(--pj-color-border-default);
  border-radius: var(--pj-radius-md);
  box-shadow: var(--pj-shadow-overlay);
  padding: 0;
  z-index: 1000;
  overflow: hidden;
}

.pj-dropdown-menu__item {
  display: flex;
  align-items: center;
  gap: var(--pj-space-3);
  width: 100%;
  min-height: 48px;
  padding: var(--pj-space-2) var(--pj-space-4);
  background: transparent;
  border: 0;
  border-bottom: var(--pj-border-width-hairline) solid var(--pj-color-brand-gray-100);
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-regular);
  color: var(--pj-color-text-default);
  line-height: var(--pj-font-line-height-base);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);

  &:last-child { border-bottom: none; }

  &:hover:not(.is-disabled) { background: var(--pj-color-brand-gray-100); }

  &:focus { outline: none; }

  &:focus-visible {
    box-shadow: inset 0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }

  &.is-disabled,
  &:disabled {
    color: var(--pj-color-text-disabled);
    cursor: not-allowed;
  }
}

.pj-dropdown-menu__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.pj-dropdown-menu__item-text {
  display: flex;
  flex-direction: column;
  gap: var(--pj-space-1);
  flex: 1;
  min-width: 0;
  text-align: left;
}

.pj-dropdown-menu__item-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* select variant — two-line selector with a check on the selected item */
.pj-dropdown-menu--select {
  .pj-dropdown-menu__menu {
    display: flex;
    flex-direction: column;
    gap: var(--pj-space-2);                    /* 8 */
    padding: var(--pj-space-2) var(--pj-space-3); /* 8 12 */
    border-radius: var(--pj-radius-xl);         /* 16 */
    border-color: var(--pj-color-brand-gray-175, #e8e8e3);
  }

  .pj-dropdown-menu__item {
    min-height: 0;
    gap: var(--pj-space-4);                     /* 16 */
    align-items: center;
    padding: var(--pj-space-2);                 /* 8 */
    border-bottom: none;
    border-radius: var(--pj-radius-md);         /* 8 */

    &:hover:not(.is-disabled) { background: var(--pj-color-brand-gray-25, #fafafa); }
    &:active:not(.is-disabled) { background: var(--pj-color-brand-gray-50, #f6f6f6); }
  }

  .pj-dropdown-menu__item-label {
    font-size: var(--pj-font-size-md);
    font-weight: var(--pj-font-weight-semibold);
    line-height: 1.25;
    letter-spacing: -0.5px;
    color: var(--pj-color-brand-gray-850, #222926);
  }

  .pj-dropdown-menu__item-description {
    font-size: var(--pj-font-size-xs);          /* 12 */
    font-weight: var(--pj-font-weight-medium);
    line-height: 1.1667;                        /* 14/12 */
    color: var(--pj-color-brand-gray-550, #74756f);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pj-dropdown-menu__item-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: var(--pj-color-brand-green-450, #2fad4c);
  }
}

.pj-dropdown-menu-fade-enter-active,
.pj-dropdown-menu-fade-leave-active {
  transition:
    opacity var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    transform var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);
}

.pj-dropdown-menu-fade-enter-from,
.pj-dropdown-menu-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .pj-dropdown-menu__chevron,
  .pj-dropdown-menu__trigger-default,
  .pj-dropdown-menu__item,
  .pj-dropdown-menu-fade-enter-active,
  .pj-dropdown-menu-fade-leave-active { transition: none; }
}
</style>
